import { Component, EventEmitter, Input, Output } from '@angular/core';

interface CalendarDay {
    iso: string;
    day: number;
    inMonth: boolean;
    isToday: boolean;
}

/** Botón tipo "pill" que muestra el rango formateado ("15 may 2026 - 15 may
 * 2026") + flecha, y al hacer click despliega un calendario visual (grid de
 * días con navegación de mes/año) para elegir el rango — en vez de dos
 * &lt;input type="date"&gt; nativos, siguiendo el mismo patrón del selector
 * de día tipo calendario ya usado en Pagos a proveedores. */
@Component({
    selector: 'app-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.css'],
    host: { '(document:click)': 'onDocumentClick($event)' }
})
export class DateRangePickerComponent {
    @Input() from = '';
    @Input() to = '';
    @Output() fromChange = new EventEmitter<string>();
    @Output() toChange = new EventEmitter<string>();
    @Output() rangeApplied = new EventEmitter<void>();

    readonly weekdayLabels = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

    open = false;
    viewYear = new Date().getFullYear();
    viewMonth = new Date().getMonth();
    draftFrom = '';
    draftTo = '';

    get label(): string {
        if (!this.from || !this.to) return 'Seleccionar fechas';
        return `${this.formatDate(this.from)} - ${this.formatDate(this.to)}`;
    }

    get monthLabel(): string {
        const label = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' }).format(new Date(this.viewYear, this.viewMonth, 1));
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    /** Array normal (NO getter): un getter referenciado desde *ngFor se
     * reevalúa en cada ciclo de detección de cambios de Angular, devolviendo
     * cada vez 42 objetos nuevos — *ngFor los ve como "todos distintos" y
     * destruye/recrea los 42 botones sin parar, lo que efectivamente cuelga
     * el navegador. Se recalcula solo cuando realmente cambia el mes. */
    calendarDays: CalendarDay[] = [];

    private buildCalendarDays(): void {
        const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
        // Lunes=0 ... Domingo=6, para empezar la grilla en lunes.
        const leadingBlank = (firstOfMonth.getDay() + 6) % 7;
        const gridStart = new Date(this.viewYear, this.viewMonth, 1 - leadingBlank);

        const todayIso = this.toIso(new Date());
        const days: CalendarDay[] = [];
        for (let i = 0; i < 42; i++) {
            const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
            const iso = this.toIso(date);
            days.push({
                iso,
                day: date.getDate(),
                inMonth: date.getMonth() === this.viewMonth,
                isToday: iso === todayIso,
            });
        }
        this.calendarDays = days;
    }

    trackByIso(_index: number, day: CalendarDay): string {
        return day.iso;
    }

    private toIso(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    private formatDate(value: string): string {
        const date = new Date(value + 'T00:00:00');
        if (isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
    }

    toggle(): void {
        if (!this.open) {
            this.draftFrom = this.from;
            this.draftTo = this.to;
            const base = this.from ? new Date(this.from + 'T00:00:00') : new Date();
            this.viewYear = base.getFullYear();
            this.viewMonth = base.getMonth();
            this.buildCalendarDays();
        }
        this.open = !this.open;
    }

    prevMonth(): void {
        this.viewMonth--;
        if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear--; }
        this.buildCalendarDays();
    }

    nextMonth(): void {
        this.viewMonth++;
        if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear++; }
        this.buildCalendarDays();
    }

    dayState(iso: string): 'start' | 'end' | 'in-range' | 'none' {
        if (!this.draftFrom) return 'none';
        if (!this.draftTo) return iso === this.draftFrom ? 'start' : 'none';
        if (iso === this.draftFrom) return 'start';
        if (iso === this.draftTo) return 'end';
        if (iso > this.draftFrom && iso < this.draftTo) return 'in-range';
        return 'none';
    }

    selectDay(iso: string): void {
        if (!this.draftFrom || this.draftTo) {
            this.draftFrom = iso;
            this.draftTo = '';
        } else if (iso < this.draftFrom) {
            this.draftTo = this.draftFrom;
            this.draftFrom = iso;
        } else {
            this.draftTo = iso;
        }
    }

    apply(): void {
        if (!this.draftFrom) return;
        const from = this.draftFrom;
        const to = this.draftTo || this.draftFrom;
        this.from = from;
        this.to = to;
        this.fromChange.emit(from);
        this.toChange.emit(to);
        this.open = false;
        this.rangeApplied.emit();
    }

    onDocumentClick(event: MouseEvent): void {
        if (!this.open) return;
        const target = event.target as HTMLElement;
        if (!target.closest('app-date-range-picker')) {
            this.open = false;
        }
    }
}
